'use strict';

var isNil = require('ramda/src/isNil');
var is = require('ramda/src/is');
var has = require('ramda/src/has');
var any = require('ramda/src/any');
var equals = require('ramda/src/equals');
var keys = require('ramda/src/keys');
var pipe = require('ramda/src/pipe');
var trim = require('ramda/src/trim');
var mergeRight = require('ramda/src/mergeRight');
var split = require('ramda/src/split');
var reject = require('ramda/src/reject');
var map = require('ramda/src/map');
var fromPairs = require('ramda/src/fromPairs');
var anyPass = require('ramda/src/anyPass');
var isEmpty = require('ramda/src/isEmpty');
var join = require('ramda/src/join');
var mapObjIndexed = require('ramda/src/mapObjIndexed');
var pick = require('ramda/src/pick');
var replace = require('ramda/src/replace');
var toUpper = require('ramda/src/toUpper');
var zipObj = require('ramda/src/zipObj');
var curry = require('ramda/src/curry');

var DEFAULT = 'REDUXSAUCE.DEFAULT';

var Types = /*#__PURE__*/Object.freeze({
  __proto__: null,
  DEFAULT: DEFAULT
});

/**
  Creates a reducer.
  @param {string} initialState - The initial state for this reducer.
  @param {object} handlers - Keys are action types (strings), values are reducers (functions).
  @return {object} A reducer object.
 */
var cr = (function (initialState, handlers) {
  // initial state is required
  if (initialState === undefined) {
    throw new Error('initial state is required');
  }

  // handlers must be an object
  if (isNil(handlers) || !is(Object, handlers)) {
    throw new Error('handlers must be an object');
  }

  // handlers cannot have an undefined key
  if (any(equals('undefined'))(keys(handlers))) {
    throw new Error('handlers cannot have an undefined key');
  }

  // create the reducer function
  return function () {
    var state = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : initialState;
    var action = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;
    // wrong actions, just return state
    if (isNil(action)) return state;
    if (!has('type', action)) return state;

    // look for the handler
    var handler = handlers[action.type] || handlers[DEFAULT];

    // no handler no cry
    if (isNil(handler)) return state;

    // execute the handler
    return handler(state, action);
  };
});

var isNilOrEmpty = anyPass([isNil, isEmpty]);
var defaultOptions$1 = {
  prefix: ''
};
var ct = (function (types) {
  var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  if (isNilOrEmpty(types)) throw new Error('valid types are required');
  var _mergeRight = mergeRight(defaultOptions$1, options),
    prefix = _mergeRight.prefix;
  return pipe(trim, split(/\s/), map(trim), reject(isNilOrEmpty), map(function (x) {
    return [x, prefix + x];
  }), fromPairs)(types);
});

function ownKeys(e, r) {
  var t = Object.keys(e);
  if (Object.getOwnPropertySymbols) {
    var o = Object.getOwnPropertySymbols(e);
    r && (o = o.filter(function (r) {
      return Object.getOwnPropertyDescriptor(e, r).enumerable;
    })), t.push.apply(t, o);
  }
  return t;
}
function _objectSpread2(e) {
  for (var r = 1; r < arguments.length; r++) {
    var t = null != arguments[r] ? arguments[r] : {};
    r % 2 ? ownKeys(Object(t), !0).forEach(function (r) {
      _defineProperty(e, r, t[r]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) {
      Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r));
    });
  }
  return e;
}
function _toPrimitive(t, r) {
  if ("object" != typeof t || !t) return t;
  var e = t[Symbol.toPrimitive];
  if (void 0 !== e) {
    var i = e.call(t, r || "default");
    if ("object" != typeof i) return i;
    throw new TypeError("@@toPrimitive must return a primitive value.");
  }
  return ("string" === r ? String : Number)(t);
}
function _toPropertyKey(t) {
  var i = _toPrimitive(t, "string");
  return "symbol" == typeof i ? i : String(i);
}
function _defineProperty(obj, key, value) {
  key = _toPropertyKey(key);
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }
  return obj;
}

var defaultOptions = {
  prefix: ''
};

// matches each word in a camelCaseString (except the first)
// consecutive capitals are treated as one word
var RX_CAPS = /(?!^)([A-Z][a-z0-9]+|[A-Z][A-Z0-9]*(?=[A-Z]|\b))/g;

// converts a camelCaseWord into a SCREAMING_SNAKE_CASE word
var camelToScreamingSnake = pipe(replace(RX_CAPS, '_$1'), toUpper);

// build Action Types out of an object
var convertToTypes = function convertToTypes(config, options) {
  var opts = mergeRight(defaultOptions, options);
  return pipe(keys,
  // just the keys
  map(camelToScreamingSnake),
  // CONVERT_THEM
  join(' '),
  // space separated
  function (types) {
    return ct(types, opts);
  } // make them into Redux Types
  )(config);
};

// an action creator with additional properties
var createActionCreator = function createActionCreator(name, extraPropNames, options) {
  var _mergeRight = mergeRight(defaultOptions, options),
    prefix = _mergeRight.prefix;
  // types are upcase and snakey
  var type = "".concat(prefix).concat(camelToScreamingSnake(name));

  // do we need extra props for this?
  var noKeys = isNil(extraPropNames) || isEmpty(extraPropNames);

  // a type-only action creator
  if (noKeys) return function () {
    return {
      type: type
    };
  };

  // an action creator with type + properties
  // "properties" is defined as an array of prop names
  if (is(Array, extraPropNames)) {
    return function () {
      for (var _len = arguments.length, values = new Array(_len), _key = 0; _key < _len; _key++) {
        values[_key] = arguments[_key];
      }
      var extraProps = zipObj(extraPropNames, values);
      return _objectSpread2({
        type: type
      }, extraProps);
    };
  }

  // an action creator with type + properties
  // "properties" is defined as an object of {prop name: default value}
  if (is(Object, extraPropNames)) {
    var defaultProps = extraPropNames;
    return function () {
      var valueObject = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
      var providedProps = pick(Object.keys(defaultProps), valueObject);
      return _objectSpread2(_objectSpread2({
        type: type
      }, defaultProps), providedProps);
    };
  }
  throw new Error('action props must be a null/array/object/function');
};

// build Action Creators out of an object
var convertToCreators = function convertToCreators(config, options) {
  return mapObjIndexed(function (num, key, value) {
    if (typeof value[key] === 'function') {
      // the user brought their own action creator
      return value[key];
    } else {
      // lets make an action creator for them!
      return createActionCreator(key, value[key], options);
    }
  })(config);
};
var ca = (function (config, options) {
  if (isNil(config)) {
    throw new Error('an object is required to setup types and creators');
  }
  if (isEmpty(config)) {
    throw new Error('empty objects are not supported');
  }
  return {
    Types: convertToTypes(config, options),
    Creators: convertToCreators(config, options)
  };
});

/**
 * Allows your reducers to be reset.
 *
 * @param {string} typeToReset - The action type to listen for.
 * @param {function} originalReducer - The reducer to wrap.
 */
function resettableReducer$1(typeToReset, originalReducer) {
  // a valid type is required
  if (!is(String, typeToReset) || typeToReset === '') {
    throw new Error('A valid reset type is required.');
  }

  // an original reducer is required
  if (typeof originalReducer !== 'function') {
    throw new Error('A reducer is required.');
  }
  // run it through first to get what the default state should be
  var resetState = originalReducer(undefined, {});

  // create our own reducer that wraps the original one and hijacks the reset
  function reducer() {
    var state = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : resetState;
    var action = arguments.length > 1 ? arguments[1] : undefined;
    if (action && action.type === typeToReset) {
      return resetState;
    } else {
      return originalReducer(state, action);
    }
  }
  return reducer;
}
var rr = curry(resettableReducer$1);

var createReducer = cr;
var createTypes = ct;
var createActions = ca;
var resettableReducer = rr;

exports.Types = Types;
exports.createActions = createActions;
exports.createReducer = createReducer;
exports.createTypes = createTypes;
exports.resettableReducer = resettableReducer;
